import { useEffect, useState } from "react";
import { AnimatePresence, motion, useScroll } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId, scrollToForm } from "../config";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const { scrollYProgress } = useScroll();

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 ${
        scrolled
          ? "border-b border-ink/10 bg-void/85 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <motion.div
        className="absolute bottom-0 left-0 h-[2px] w-full origin-left bg-mint"
        style={{ scaleX: scrollYProgress }}
      />
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6">
        <button
          data-testid="nav-logo"
          onClick={() => window.__lenis?.scrollTo(0, { duration: 1.2 })}
          className="flex flex-col items-start leading-none"
        >
          <span className={`font-display text-xl font-extrabold tracking-tight transition-colors duration-500 ${scrolled ? "text-ink" : "text-[#FFFFFF]"}`}>cardconnect</span>
          <span className="font-mono text-[9px] tracking-[0.45em] text-mint">COMMERCE</span>
        </button>
        <nav className={`hidden items-center gap-10 font-mono text-sm font-bold uppercase tracking-[0.15em] transition-colors duration-500 md:flex ${scrolled ? "text-ink/70" : "text-[#FFFFFF]/80"}`}>
          <button data-testid="nav-how" onClick={() => scrollToId("how-it-works")} className="transition-colors hover:text-mint">How It Works</button>
          <button data-testid="nav-results" onClick={() => scrollToId("proof")} className="transition-colors hover:text-mint">Customer Results</button>
          <button data-testid="nav-faq" onClick={() => scrollToId("faq")} className="transition-colors hover:text-mint">FAQs</button>
        </nav>
        <div className="flex items-center gap-3">
          <button
            data-testid="nav-cta"
            onClick={scrollToForm}
            className="rounded-full bg-mint px-5 py-2.5 font-display text-sm font-bold text-cream transition-transform duration-300 hover:scale-105 hover:brightness-110 max-sm:px-4 max-sm:text-xs"
          >
            Request My Free Review
          </button>
          <button
            data-testid="nav-menu-button"
            aria-label="Open menu"
            onClick={() => setMenuOpen((o) => !o)}
            className={`flex h-10 w-10 items-center justify-center rounded-full border transition-colors md:hidden ${
              scrolled || menuOpen ? "border-ink/20 text-ink" : "border-[#FFFFFF]/40 text-[#FFFFFF]"
            }`}
          >
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            data-testid="mobile-nav"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-b border-ink/10 bg-void/95 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4 font-mono text-sm font-bold uppercase tracking-[0.15em]">
              {[
                ["How It Works", "how-it-works"],
                ["Customer Results", "proof"],
                ["FAQs", "faq"],
                ["Request My Free Review", "hero-form"],
              ].map(([label, id]) => (
                <button
                  key={id}
                  data-testid={`mobile-nav-${id}`}
                  onClick={() => {
                    setMenuOpen(false);
                    scrollToId(id);
                  }}
                  className="rounded-xl px-4 py-3.5 text-left text-cream/80 transition-colors hover:bg-mint/10 hover:text-mint"
                >
                  {label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
